import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  useSectionRoster, useRemoveStudentsFromSection, useRollNumbers, useSectionAutoGroups, useSectionsList, useBulkAssignSection,
} from '../../../api/hooks';
import AddStudentsModal from './AddStudentsModal';
import { personName } from '../../../lib/names';
import { Button, Checkbox, Initials, Pager, SortableTh, Table, Td, Th } from '../../../components/ui';
import { FaPlus } from '../../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import usePagination from '../../../lib/usePagination';
import type { SectionRosterEntry } from '../../../api/types';
import { formatRoll } from '../../../lib/roll';

interface Props {
  sectionId: number;
  gradeId: number;
  canEdit: boolean;
}

// Roster status from the 12-week overall average: under 50 is below the pass mark, 75 and over is excelling.
export function rosterStatus(avg: number | null | undefined): { label: string; cls: string } | null {
  if (avg == null) return null;
  if (avg < 50) return { label: 'Below pass', cls: 'bg-danger-bg text-danger-ink' };
  if (avg >= 75) return { label: 'Excelling', cls: 'bg-success-bg text-success' };
  return { label: 'On track', cls: 'bg-mist text-muted-2' };
}

const ROSTER_COLUMNS: SortColumn<SectionRosterEntry>[] = [
  { key: 'name', accessor: (r) => personName(r).toLowerCase() },
];

export default function RosterTab({ sectionId, gradeId }: Props) {
  // `canEdit` is part of the props contract (Task 10 refactor preparing for
  // Task 11's tab shell); the current roster UI does not gate on it yet, so
  // it's intentionally not destructured here. Future tasks will use it.
  const rosterQ = useSectionRoster(sectionId);
  const rollQ = useRollNumbers(gradeId);
  const rollOf = new Map((rollQ.data?.students ?? []).map((r) => [r.student_id, r.roll_number]));
  const removeMut = useRemoveStudentsFromSection(sectionId);
  const moveMut = useBulkAssignSection();
  const qc = useQueryClient();
  const [moveError, setMoveError] = useState<string | null>(null);
  const sectionsQ = useSectionsList(gradeId);
  // Same query (and cache entry) as the Groups tab; its overall_avg gives each student's Average and Status.
  const autoQ = useSectionAutoGroups(sectionId, { weeks: 12, threshold: 50 });
  const avgOf = new Map<number, number | null>();
  for (const b of autoQ.data?.overall_bands ?? []) for (const s of b.students) avgOf.set(s.id, s.overall_avg ?? null);
  const showAvg = !!autoQ.data;
  const targets = (sectionsQ.data?.sections ?? []).filter((s) => s.id !== sectionId);
  const [adding, setAdding] = useState(false);
  const [rosterSelected, setRosterSelected] = useState<Set<number>>(new Set());
  const { rows, sort, toggle } = useSortableTable(rosterQ.data?.students, ROSTER_COLUMNS, { key: 'name', dir: 'asc' });

  const pg = usePagination(rows, 10, `${sort?.key}|${sort?.dir}`);
  const total = rosterQ.data?.students.length ?? 0;
  const allChecked = total > 0 && rows.every((r) => rosterSelected.has(r.user_id));

  function toggleRoster(id: number) {
    setRosterSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setRosterSelected(allChecked ? new Set() : new Set(rows.map((r) => r.user_id)));
  }

  async function removeSelected() {
    if (!rosterSelected.size) return;
    await removeMut.mutateAsync({ student_ids: Array.from(rosterSelected) });
    setRosterSelected(new Set());
  }

  async function moveSelected(targetId: number) {
    if (!rosterSelected.size) return;
    setMoveError(null);
    const ids = Array.from(rosterSelected);
    try {
      const res = await moveMut.mutateAsync({ student_ids: ids, section_id: targetId });
      // The hook only refreshes the students and section lists: refresh this section and the target too.
      qc.invalidateQueries({ queryKey: ['school', 'section', sectionId] });
      qc.invalidateQueries({ queryKey: ['school', 'section', targetId] });
      const failed = new Set((res?.errors ?? []).map((e) => e.student_id));
      if (failed.size > 0) {
        const names = rows.filter((r) => failed.has(r.user_id)).map((r) => personName(r));
        setRosterSelected(new Set(ids.filter((i) => failed.has(i))));
        setMoveError(`${failed.size} could not be moved${names.length ? `: ${names.join(', ')}` : ''}.`);
        return;
      }
      setRosterSelected(new Set());
    } catch {
      setMoveError('Could not move the selected students. Try again.');
    }
  }

  return (
    <>
      {rosterSelected.size > 0 ? (
        <div className="mb-3.5 flex flex-wrap items-center gap-2.5 rounded-[14px] bg-navy px-3 py-2.5 text-white">
          <span className="mr-1.5 font-semibold">{rosterSelected.size} selected</span>
          {targets.length > 0 && <span className="text-[13px] text-on-navy-muted">Move to</span>}
          {targets.map((t) => (
            <Button key={t.id} variant="on-navy" size="sm" disabled={moveMut.isPending} onClick={() => moveSelected(t.id)}>
              Section {t.name} · {t.student_count} students
            </Button>
          ))}
          <span className="flex-1" />
          <Button variant="on-navy" size="sm" onClick={removeSelected} disabled={removeMut.isPending}>
            Remove from section
          </Button>
        </div>
      ) : (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
          <span className="text-[13px] text-muted">Select students to move them to another section.</span>
          <Button variant="secondary" size="sm" className="min-h-[38px]" icon={<FaPlus size={18} strokeWidth={1.8} />} onClick={() => setAdding(true)}>
            Add students
          </Button>
        </div>
      )}

      {moveError && <p role="alert" className="mb-3 rounded-[12px] bg-danger-bg p-3 text-sm text-danger-ink">{moveError}</p>}
      <Table minWidth={560}>
        <thead>
          <tr>
            <Th className="w-[34px]">
              <Checkbox
                size={20}
                aria-label="Select all students"
                checked={allChecked}
                disabled={total === 0}
                onChange={toggleAll}
              />
            </Th>
            <SortableTh columnKey="name" sort={sort} onToggle={toggle} className="border-b border-line">Student</SortableTh>
            {rollQ.data && <Th align="right">Roll no.</Th>}
            {showAvg && <Th align="right">Average</Th>}
            {showAvg && <Th>Status</Th>}
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((r) => {
            const avg = avgOf.get(r.user_id);
            const st = rosterStatus(avg);
            return (
              <tr key={r.user_id} className={rosterSelected.has(r.user_id) ? '[&_td]:bg-row-sel' : ''}>
                <Td>
                  <Checkbox
                    size={20}
                    aria-label={`Select ${r.first_name} ${r.last_name}`}
                    checked={rosterSelected.has(r.user_id)}
                    onChange={() => toggleRoster(r.user_id)}
                  />
                </Td>
                <Td>
                  <Link to={`/students/profile/${r.user_id}`} className="inline-flex items-center gap-2.5 font-semibold text-ink no-underline hover:underline">
                    <Initials name={personName(r)} className="!h-[30px] !w-[30px] !rounded-[10px] !text-[11px]" />
                    <span>{personName(r)}</span>
                  </Link>
                </Td>
                {rollQ.data && (
                  <Td align="right" className="font-mono text-ink">{formatRoll(rollOf.get(r.user_id))}</Td>
                )}
                {showAvg && (
                  <Td align="right" className="font-mono text-ink">{avg == null ? '—' : `${Math.round(avg)}%`}</Td>
                )}
                {showAvg && (
                  <Td>
                    {st ? <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span> : <span className="text-muted">—</span>}
                  </Td>
                )}
              </tr>
            );
          })}
          {!rosterQ.isLoading && total === 0 && (
            <tr><td colSpan={2 + (rollQ.data ? 1 : 0) + (showAvg ? 2 : 0)} className="px-2.5 py-4 text-sm text-muted">No students yet.</td></tr>
          )}
        </tbody>
      </Table>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted">
          {pg.pageCount > 1 ? `Showing ${pg.from}–${pg.to} of ${total} students` : `Showing ${total} ${total === 1 ? 'student' : 'students'}`} ·{' '}
          <Link to="/students" className="font-semibold text-ink hover:underline">open full roster</Link>
        </p>
        <Pager page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} />
      </div>

      {adding && (
        <AddStudentsModal
          gradeId={gradeId}
          sectionId={sectionId}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}
